/**
 * AuthProvider — manages JWT authentication state for the entire app.
 *
 * Token lifecycle:
 * 1. On mount: reads the stored access token and hydrates user state via GET /api/auth/me.
 *    If the token is invalid/expired, both tokens are cleared and the user is treated as
 *    unauthenticated (ProtectedRoute will redirect to /login/).
 * 2. On login: stores accessToken + refreshToken in localStorage, sets user state.
 *    Decodes the JWT exp claim and schedules a proactive silent refresh 2 minutes before
 *    expiry so mid-session API calls never hit an expired token.
 * 3. Silent refresh (apiFetch): apiFetch.ts calls the registered onRefreshSuccess /
 *    onAuthFailure handlers on every 401. If successful the new token is stored and the
 *    original request is retried once. If the refresh token is also expired, logout() is
 *    called automatically and the user is redirected to /login/.
 * 4. On logout: removes both tokens, clears user state, navigates explicitly to /login/.
 *
 * Security note:
 *   Tokens are stored in localStorage (accessible to JS on the same origin).
 *   A future upgrade path is httpOnly cookies (requires server-side Set-Cookie handling
 *   and CORS credentials config). The short 15-min access token + silent refresh
 *   strategy minimises the exposure window for now.
 */

import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { getMe, refreshToken as refreshTokenApi } from '../lib/api';
import { registerAuthHandlers } from '../lib/apiFetch';

type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  companyId: string;
  divisionId?: string;
  isActive: boolean;
};

export type AuthContextValue = {
  user: AuthUser | null;
  /** Current access token — use this instead of reading localStorage directly. */
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithPassword: (email: string, password: string, companyId?: string) => Promise<void>;
  logout: () => void;
};

const ACCESS_TOKEN_KEY = 'pelajari_access_token';
const REFRESH_TOKEN_KEY = 'pelajari_refresh_token';

/** Decode the `exp` claim from a JWT without any external library. */
function getTokenExp(tok: string): number | null {
  try {
    const payload = tok.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Refresh timer ────────────────────────────────────────────────────────
  // Proactively refreshes the access token 10 minutes before it expires so
  // in-flight API calls always have a valid token. With 60m tokens this fires
  // at the 50-minute mark, giving a generous window.

  const scheduleRefresh = useCallback((accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const exp = getTokenExp(accessToken);
    if (!exp) return;

    const msUntilRefresh = exp * 1000 - Date.now() - 10 * 60 * 1000;
    if (msUntilRefresh <= 0) return; // already near expiry — apiFetch handles the 401

    refreshTimerRef.current = setTimeout(async () => {
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefresh) return;
      try {
        const res = await refreshTokenApi(storedRefresh);
        if (res.success && res.data?.accessToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, res.data.accessToken);
          if (res.data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, res.data.refreshToken);
          setToken(res.data.accessToken);
          scheduleRefresh(res.data.accessToken);
        }
        // If it failed silently, apiFetch.ts will handle the next 401
      } catch {
        // Network error — apiFetch will handle the next 401
      }
    }, msUntilRefresh);
  }, []);

  // ── Auth failure (called by apiFetch on unrecoverable 401) ───────────────

  const handleAuthFailure = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
    setToken(null);
    navigate({ to: '/login/' });
  }, [navigate]);

  // ── Bootstrap on mount ───────────────────────────────────────────────────
  // On page load / tab open:
  //   1. Try the stored access token via GET /api/auth/me.
  //   2. If it fails (expired or invalid) but a refresh token exists, attempt
  //      a silent refresh before giving up — this covers the case where the
  //      user left the tab idle for >60 min and the access token expired but
  //      the 7-day refresh token is still valid.
  //   3. If both fail, clear storage and navigate to /login/ immediately (don't
  //      wait for ProtectedRoute to react — avoids blank/stuck screens).

  useEffect(() => {
    let cancelled = false;

    async function bootstrapAuth() {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      // Step 1 — validate existing access token
      const response = await getMe(accessToken);
      if (cancelled) return;

      if (response.success && response.data) {
        setUser(response.data.user);
        setToken(accessToken);
        scheduleRefresh(accessToken);
        setIsLoading(false);
        return;
      }

      // Step 2 — access token rejected; try refreshing before giving up
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (storedRefresh) {
        try {
          const refreshRes = await refreshTokenApi(storedRefresh);
          if (!cancelled && refreshRes.success && refreshRes.data?.accessToken) {
            const newAccess = refreshRes.data.accessToken;
            const newRefresh = refreshRes.data.refreshToken ?? storedRefresh;
            localStorage.setItem(ACCESS_TOKEN_KEY, newAccess);
            localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);

            // Validate the new token to hydrate user state
            const meRes = await getMe(newAccess);
            if (!cancelled && meRes.success && meRes.data) {
              setUser(meRes.data.user);
              setToken(newAccess);
              scheduleRefresh(newAccess);
              setIsLoading(false);
              return;
            }
          }
        } catch {
          // network error during bootstrap refresh — fall through to logout
        }
      }

      if (cancelled) return;

      // Step 3 — both tokens unusable: clear and redirect immediately
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
      setToken(null);
      setIsLoading(false);
      navigate({ to: '/login/' });
    }

    bootstrapAuth();
    return () => { cancelled = true; };
  }, [scheduleRefresh, navigate]);

  // ── Register handlers with apiFetch ─────────────────────────────────────
  // Re-registers whenever token/logout refs change so apiFetch always has
  // the latest token getter and the correct failure callback.

  useEffect(() => {
    registerAuthHandlers({
      getToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
      getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
      onRefreshSuccess: (newAccessToken, newRefreshToken) => {
        localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        setToken(newAccessToken);
        scheduleRefresh(newAccessToken);
      },
      onAuthFailure: handleAuthFailure,
    });
  }, [scheduleRefresh, handleAuthFailure]);

  // ── Login ────────────────────────────────────────────────────────────────

  const loginWithPassword = useCallback(async (
    email: string,
    password: string,
    companyId?: string,
  ) => {
    const { login } = await import('../lib/api');
    const response = await login(email, password, companyId);

    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Login failed');
    }

    const { accessToken, refreshToken } = response.data.tokens;
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    setUser(response.data.user);
    setToken(accessToken);
    scheduleRefresh(accessToken);
  }, [scheduleRefresh]);

  // ── Logout ───────────────────────────────────────────────────────────────

  const logout = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
    setToken(null);
    navigate({ to: '/login/' });
  }, [navigate]);

  // ── Context value ────────────────────────────────────────────────────────

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: !!user,
      isLoading,
      loginWithPassword,
      logout,
    }),
    [user, token, isLoading, loginWithPassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
